/**
 * useEmailAccounts Hook
 *
 * Fetches and manages the user's email accounts. Provides the "active" account
 * used by all other email hooks.
 */

import { useState, useCallback, useEffect } from 'react';
import { api, EmailAccount } from '@/lib/api';

export function useEmailAccounts() {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [activeAccount, setActiveAccount] = useState<EmailAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listEmailAccounts();
      setAccounts(result.accounts);
      // Auto-select primary account if none active
      if (result.accounts.length > 0 && !activeAccount) {
        const primary = result.accounts.find((a) => a.isPrimary) || result.accounts[0];
        setActiveAccount(primary);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email accounts');
    } finally {
      setLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    fetchAccounts();
  }, []); // intentionally runs once on mount

  const selectAccount = useCallback(
    (accountId: string) => {
      const account = accounts.find((a) => a.id === accountId);
      if (account) setActiveAccount(account);
    },
    [accounts],
  );

  return {
    accounts,
    activeAccount,
    loading,
    error,
    fetchAccounts,
    selectAccount,
  };
}
